import React from "react";
import {
    Box, Button, Flex, Icon, Spinner, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdAccountBalance, MdRefresh } from "react-icons/md";
import ChannelCard from "./ChannelCard";
import CrossCurrencyPanel from "./CrossCurrencyPanel";
import useTreasuryChannels from "./useTreasuryChannels";

/**
 * Renders the page header with a brand icon badge, title and intro text.
 * @param {Object} props - generatedAt timestamp and the refresh handler
 * @returns {React.ReactElement}
 */
function PageHeader({ generatedAt, onRefresh, loading }) {
    const badgeBg = useColorModeValue("brand.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const stamp = generatedAt ? new Date(generatedAt).toLocaleString("fr-FR") : null;

    return (
        <Flex align="center" justify="space-between" gap="12px" mb="20px" wrap="wrap">
            <Flex align="center" gap="12px">
                <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={badgeBg} flexShrink="0">
                    <Icon as={MdAccountBalance} w="24px" h="24px" color="brand.500" />
                </Flex>
                <Box>
                    <Text fontSize="lg" fontWeight="700" color={titleColor}>Trésorerie par canal</Text>
                    <Text fontSize="sm" color={subColor}>
                        Position de chaque canal d'encaissement. Orange Money et Stripe ne
                        peuvent pas se financer l'un l'autre : ce que l'un encaisse ne peut
                        pas régler ce que l'autre doit.
                    </Text>
                </Box>
            </Flex>
            <Flex align="center" gap="10px">
                {stamp && <Text fontSize="xs" color={subColor}>Arrêté au {stamp}</Text>}
                <Button size="sm" leftIcon={<Icon as={MdRefresh} />} onClick={onRefresh} isLoading={loading}>
                    Actualiser
                </Button>
            </Flex>
        </Flex>
    );
}

/**
 * Groups the normalized currency rows by the rail that settles them.
 * @param {Array} currencies - normalized currency rows
 * @returns {Array} list of [channel, rows] pairs
 */
function groupByChannel(currencies) {
    const grouped = currencies.reduce((acc, row) => {
        acc[row.channel] = acc[row.channel] || [];
        acc[row.channel].push(row);
        return acc;
    }, {});
    return Object.entries(grouped);
}

/**
 * Renders the empty, loading and error states of the page.
 * @param {Object} props - loading flag, error payload and emptiness flag
 * @returns {React.ReactElement|null}
 */
function PageState({ loading, error, isEmpty }) {
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading) {
        return (
            <Flex justify="center" py="40px"><Spinner color="brand.500" /></Flex>
        );
    }
    if (error) {
        return (
            <Text fontSize="sm" color="red.400" py="20px">
                {typeof error === "string" ? error : "Impossible de charger la trésorerie"}
            </Text>
        );
    }
    if (isEmpty) {
        return (
            <Text fontSize="sm" color={hintColor} py="20px">
                Aucun encaissement ni engagement enregistré pour le moment.
            </Text>
        );
    }
    return null;
}

/**
 * Treasury monitoring page: one card per settlement rail, plus the
 * cross-currency flows that explain any imbalance between them.
 * @returns {React.ReactElement}
 */
function Treasury() {
    const { report, loading, error, refresh } = useTreasuryChannels();
    const currencies = report?.currencies || [];
    const isEmpty = !loading && !error && currencies.length === 0;

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
            <PageHeader generatedAt={report?.generatedAt} onRefresh={refresh} loading={loading} />
            <PageState loading={loading} error={error} isEmpty={isEmpty} />
            {!loading && !error && currencies.length > 0 && (
                <>
                    {groupByChannel(currencies).map(([channel, rows]) => (
                        <ChannelCard key={channel} channel={channel} rows={rows} />
                    ))}
                    <CrossCurrencyPanel flows={report?.crossCurrency || []} />
                </>
            )}
        </Box>
    );
}

export default Treasury;
